type UnknownRecord = Record<string, unknown>;

export interface TravelEntryLike {
  work_item_id?: string | number | null;
  started_at?: string | null;
  ended_at?: string | null;
  metadata?: unknown;
}

export interface GroupedTravelSessionResolution {
  sessionKey: string;
  groupedAppointmentIds: string[];
  representativeWorkItemId: string | null;
  distanceKm: number;
}

function parseObject(value: unknown): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parseObject(parsed);
      } catch {
        return {};
      }
    }

    return {};
  }

  return value as UnknownRecord;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toTrimmedString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return Array.from(new Set(
      value
        .map((entry) => toTrimmedString(entry))
        .filter((entry): entry is string => Boolean(entry))
    ));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        return normalizeStringArray(JSON.parse(trimmed));
      } catch {
        return [];
      }
    }

    return Array.from(new Set(
      trimmed
        .split(',')
        .map((entry) => toTrimmedString(entry))
        .filter((entry): entry is string => Boolean(entry))
    ));
  }

  return [];
}

export function parseTravelMetadata(metadata: unknown): UnknownRecord {
  return parseObject(metadata);
}

export function getTravelDistanceKmFromMetadata(metadata: unknown): number {
  const parsed = parseTravelMetadata(metadata);
  const startOdometer = toFiniteNumber(parsed.start_odometer ?? parsed.startOdometer);
  const endOdometer = toFiniteNumber(parsed.end_odometer ?? parsed.endOdometer);

  if (startOdometer != null && endOdometer != null && endOdometer > startOdometer) {
    return Math.round((endOdometer - startOdometer) * 100) / 100;
  }

  const directDistance = toFiniteNumber(parsed.distance_km);
  if (directDistance != null && directDistance > 0) {
    return Math.round(directDistance * 100) / 100;
  }

  const estimatedDistance = toFiniteNumber(parsed.estimated_distance_km);
  if (estimatedDistance != null && estimatedDistance > 0) {
    return Math.round(estimatedDistance * 100) / 100;
  }

  return 0;
}

export function getGroupedTravelEntryAppointmentIds(entry: TravelEntryLike): string[] {
  const parsed = parseTravelMetadata(entry.metadata);
  return normalizeStringArray(parsed.grouped_appointment_ids).sort();
}

export function getGroupedTravelSessionKey(entry: TravelEntryLike): string | null {
  const parsed = parseTravelMetadata(entry.metadata);
  const explicitKey = toTrimmedString(parsed.group_travel_session_key);
  if (explicitKey) {
    return explicitKey;
  }

  const groupedAppointmentIds = getGroupedTravelEntryAppointmentIds(entry);
  if (groupedAppointmentIds.length <= 1) {
    return null;
  }

  const startedAt = toTrimmedString(entry.started_at) || '';
  const startOdometer = toTrimmedString(parsed.start_odometer ?? parsed.startOdometer) || '';
  return `travel:${groupedAppointmentIds.join(',')}|start:${startedAt}|odo:${startOdometer}`;
}

function getTravelDistanceRank(metadata: UnknownRecord): number {
  const startOdometer = toFiniteNumber(metadata.start_odometer ?? metadata.startOdometer);
  const endOdometer = toFiniteNumber(metadata.end_odometer ?? metadata.endOdometer);
  if (startOdometer != null && endOdometer != null && endOdometer > startOdometer) {
    return 3;
  }

  const directDistance = toFiniteNumber(metadata.distance_km);
  if (directDistance != null && directDistance > 0) {
    return 2;
  }

  const estimatedDistance = toFiniteNumber(metadata.estimated_distance_km);
  if (estimatedDistance != null && estimatedDistance > 0) {
    return 1;
  }

  return 0;
}

function getComparisonTimestamp(entry: TravelEntryLike): number {
  const endedAt = toTrimmedString(entry.ended_at);
  if (endedAt) {
    const parsed = new Date(endedAt).getTime();
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  const startedAt = toTrimmedString(entry.started_at);
  if (startedAt) {
    const parsed = new Date(startedAt).getTime();
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
}

export function resolveGroupedTravelSessions(
  entries: TravelEntryLike[],
  resolveRepresentativeWorkItemId?: (groupedAppointmentIds: string[]) => string | null,
): Map<string, GroupedTravelSessionResolution> {
  const groupedSessions = new Map<string, GroupedTravelSessionResolution>();
  const sessionRanks = new Map<string, number>();
  const sessionTimestamps = new Map<string, number>();

  for (const entry of entries) {
    const sessionKey = getGroupedTravelSessionKey(entry);
    if (!sessionKey) {
      continue;
    }

    const metadata = parseTravelMetadata(entry.metadata);
    const groupedAppointmentIds = getGroupedTravelEntryAppointmentIds(entry);
    if (groupedAppointmentIds.length === 0) {
      continue;
    }

    const rank = getTravelDistanceRank(metadata);
    const distanceKm = getTravelDistanceKmFromMetadata(metadata);
    const comparisonTimestamp = getComparisonTimestamp(entry);
    const previousRank = sessionRanks.get(sessionKey) ?? -1;
    const previousTimestamp = sessionTimestamps.get(sessionKey) ?? -1;

    if (
      groupedSessions.has(sessionKey)
      && (rank < previousRank || (rank === previousRank && comparisonTimestamp < previousTimestamp))
    ) {
      continue;
    }

    groupedSessions.set(sessionKey, {
      sessionKey,
      groupedAppointmentIds,
      representativeWorkItemId: resolveRepresentativeWorkItemId?.(groupedAppointmentIds)
        ?? groupedAppointmentIds[0]
        ?? null,
      distanceKm,
    });
    sessionRanks.set(sessionKey, rank);
    sessionTimestamps.set(sessionKey, comparisonTimestamp);
  }

  return groupedSessions;
}
